import {
  Box, Image,
  Switch,
  HStack,
  Text,
  CloseButton,
} from "@chakra-ui/react";
import { pxToRem } from "../../utils/editor.ts";
import { useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import useOutlineStore from "../../stores/modules/outline.ts";
import { PaperService } from "../../services/paperService.ts";
import { Document, Page, pdfjs } from "react-pdf";
import ToggleSwitch from "./Switch.tsx";

interface PdfReviewModalProps {
  isPaperViewerOpen: boolean
  onPaperViewerClose: (isOpen: boolean) => void,
  paperId: string,
  sectionNode?: any
}

// 定义暴露给父组件的方法接口
export interface PdfReviewModalRef {
  triggerCompile: (mode?: 'single' | 'double') => void;
}

export const PdfReviewModal= forwardRef<PdfReviewModalRef, PdfReviewModalProps>(({                                                                isPaperViewerOpen,
                                                                onPaperViewerClose,
                                                                paperId,
                                                                sectionNode
                                                              },ref) => {
  const [content, setContent] = useState('')
  const { currentSectionIndex, outline } = useOutlineStore()
  
  // 配置pdfjs worker
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@4.8.69/build/pdf.worker.min.mjs`;
  
  // PDF文档选项，支持中文字符
  const pdfOptions = {
    cMapUrl: `//unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
  };
  
  const [err, setError] = useState('')
  const [numPages, setNumPages] = useState<number | null>(null);
  // 添加Auto开关状态
  const [isAutoEnabled, setIsAutoEnabled] = useState<boolean>(false);
  
  // 添加拖曳功能所需的状态
  // 添加获取初始位置的函数
  const getInitialPosition = () => {
    const modalWidth = 490;
    const padding = 1; // 距离屏幕边缘的间距
    return {
      x: Math.max(padding, window.innerWidth - modalWidth - padding),
      y: 120
    };
  };
  
  // 使用函数设置初始位置
  const [position, setPosition] = useState(getInitialPosition());
  
  // 监听窗口大小变化，确保模态框始终在可见区域内
  useEffect(() => {
    const handleResize = () => {
      const modalWidth = 490;
      const padding = 1;
      const maxX = Math.max(padding, window.innerWidth - modalWidth - padding);
      
      setPosition(prev => ({
        x: Math.min(prev.x, maxX),
        y: prev.y
      }));
    };
  
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const boxRef = useRef<HTMLDivElement>(null);

  const compileSectionPdf = async (mode: 'single' | 'double') => {
    if (!outline?.nodes?.length && !sectionNode) return
    setContent('')
    setError('')
    const section = sectionNode || outline.nodes[currentSectionIndex]
    try {
      const res = await PaperService.compilePdf({
        sectionNode: section,
        paperStyle: mode,
        paperId
      })
      if (res?.compile_message) {
        setError(res?.compile_message)
        return
      }
      previewPDF(res.base64_pdf_string)
    } catch (err) {
      setError(err?.response?.data?.compile_message)
      console.error('Failed to compile PDF:', err)
    }
  }
  useEffect(() => {
    if (!isPaperViewerOpen) return
    // 读取localStorage中保存的autoPreview设置
    const autoPreview: Record<string, string> = JSON.parse(localStorage.getItem('autoPreview') || '{}')
    
    const savedAutoState = Boolean(autoPreview[paperId])
    setIsAutoEnabled(savedAutoState)
    compileSectionPdf('single')
  }, [isPaperViewerOpen]) // 添加依赖

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    triggerCompile: (mode: 'single' | 'double' = 'single') => {
      compileSectionPdf(mode);
    }
  }), [compileSectionPdf]);

  function previewPDF(base64Data: string) {
    const base64String = base64Data.startsWith('data:application/pdf;base64,')
      ? base64Data.split(',')[1]
      : base64Data;

    const binaryString = atob(base64String);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // 创建Blob对象
    const blob = new Blob([bytes], { type: 'application/pdf' });

    const pdfUrl = URL.createObjectURL(blob);
    setContent(pdfUrl)
  }

  const onChangeAuto = (e): void => {
    const checked = e.target.checked
    const autoPreview: Record<string, string> = JSON.parse(localStorage.getItem('autoPreview') || '{}')

    autoPreview[paperId] = checked
    setIsAutoEnabled(checked) // 更新本地状态
    
    localStorage.setItem('autoPreview', JSON.stringify(autoPreview))
  }

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  // 添加拖曳相关的事件处理函数
  const handleMouseDown = (e: React.MouseEvent) => {
    if (boxRef.current && e.target === boxRef.current.querySelector('header')) {
      setIsDragging(true);
      const rect = boxRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  if (!isPaperViewerOpen) return null;

  return (
    <Box
      ref={boxRef}
      position="fixed"
      left={`${position.x}px`}
      top={`${position.y}px`}
      width="490px"
      height="80vh"
      bg="white"
      boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
      borderRadius="8px"
      zIndex={1000}
      overflow="hidden"
      onMouseDown={handleMouseDown}
    >
      {/* Header */}
      <Box 
        as="header"
        p={4} 
        borderBottom="1px solid #E2E8F0"
        display="flex" 
        alignItems="center"
        justifyContent="space-between"
        cursor="move"
      >
        <HStack >
          <Box mr={'-50px'}>Preview PDF</Box>
          <ToggleSwitch onChange={(mode) => {
            compileSectionPdf(mode)
          }}/>
          {/* Auto开关 */}
          <HStack 
            spacing={2} 
            w={'70px'}
            h={'40px'}
            justify={'center'}
            borderRadius="6px"
            transition="all 0.2s"
            _hover={{
              bg: "rgba(29, 138, 255, 0.15)",
              borderColor: "rgba(29, 138, 255, 0.3)"
            }}
          >
            <Switch 
              colorScheme="blue"
              isChecked={isAutoEnabled}
              onChange={onChangeAuto}
            />
            <Text 
              userSelect="none"
              m={0}
            >
              Auto
            </Text>
          </HStack>
        </HStack>
        
        <CloseButton 
          _focusVisible={{ boxShadow: 'none' }}
          bg={'#FFFFFF'}
          width={pxToRem(24)}
          height={pxToRem(24)}
          _hover={{ bg: '#D3D7E2' }}
          color={'#8E9ABC'}
          fontSize={'8px'}
          borderRadius={'50%'}
          onClick={() => onPaperViewerClose(false)}
        />
      </Box>
      
      {/* Body */}
      <Box width={'full'} height={'calc(100% - 80px)'} p={4}>
        <Box w={'100%'} h={'100%'}
             sx={{
               ".react-pdf__Document": {
                 overflowY: 'auto',
                 overflowX: 'hidden',
                 height: '100%',
                 width: '100%',
               },
               ".react-pdf__Page": {
                 width: '100%',
                 marginBottom: '1rem'
               },
               canvas: {
                 width: '98%!important',
                 border: '2px solid #D3D7E2',
                 borderRadius: pxToRem(24)
               }
             }}>
          {err ? (
            <Box whiteSpace="pre-wrap" h="full" overflowY={'auto'}>{err}</Box>
          ) : content ? (
            <Document
              file={content}
              options={pdfOptions}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={(error) => console.error('Error loading PDF:', error)}
            >
              {Array.from(new Array(numPages), (_, index) => (
                <Page
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  width={450}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              ))}
            </Document>
          ) : (
            <Image
              mt={'40%'}
              mx={'auto'}
              w={pxToRem(60)}
              objectFit="cover"
              src="/static/loading-animate.svg"
              transform={"scale(2)"}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
})